import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { EveFooter, EveHeader } from '@ver-uds/e-verify';
import { Alert, Button, ButtonGroup, Card, CardGroup } from '@ver-uds/uswds-react';
import { User } from '@vis-auth/vis-user-client';
import { useAuthState } from '@vis-auth/react';
import ExternalLinks from '../../constants/ExternalLinks';
import loginDotGovImage from '../../images/loginDotGovCompact.svg';
import './MfaMigration.scss';
import { useEnvironment } from '../../context/Environment/EnvironmentContext';
import { useLoginDotGovVisAuthService } from '../../services/VisAuthService';
import AuthScopes from '../../constants/AuthScopes';
import { UserProvider, useUser } from '../../context/User/UserContext';
import { AccountProvider } from '../../context/Account/AccountContext';

const EVerify: React.FC = () => <span className="nobr">E-Verify</span>;

export const MfaMigration: React.FC = () => {
  const { accessToken, user } = useAuthState();
  const { LOGIN_DOT_GOV_AUTH_CLIENTID } = useEnvironment();
  const visAuthService = useLoginDotGovVisAuthService();

  const [loadingMigration, setLoadingMigration] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const fullUser = useUser() as User;

  const onMigrateClick: () => void = async () => {
    if (accessToken && LOGIN_DOT_GOV_AUTH_CLIENTID && !loadingMigration) {
      try {
        setLoadingMigration(true);
        if (user?.user_id) {
          localStorage.setItem('MFAMigrationComplete', user.user_id);
        }
        await visAuthService.authorizeMigrateRedirect(
          accessToken,
          LOGIN_DOT_GOV_AUTH_CLIENTID,
          AuthScopes.LOGIN_DOT_GOV,
        );
      } catch (e) {
        setErrorMessage('An error occurred with our system. Please try again.');
        setLoadingMigration(false);
      }
    } else {
      setErrorMessage('An error occurred with our system. Please try again.');
    }
  };

  return (
    <>
      <Helmet>
        <title>Migrate Your Account | E-Verify</title>
      </Helmet>

      <div className="mfa-migration-page display-flex flex-column flex-align-center height-viewport width-full bg-base-lightest">
        <div className="width-full">
          <EveHeader
            userMenuLabel={fullUser ? `${fullUser.firstName} ${fullUser.lastName}` : undefined}
            showSelfTestLink={false}
            hideNavItems
            hideNavigateToDashboard
          />
        </div>

        <main className="bg-white width-full flex-1">
          <div className="grid-container margin-top-4 margin-bottom-4">
            <CardGroup>
              <Card flag className="width-full">
                <Card.Media>
                  <img src={loginDotGovImage} alt="Login.gov logo" className="padding-left-3 padding-right-3" />
                </Card.Media>

                <Card.Header as="h3">You must migrate your account to sign in with Login.gov</Card.Header>

                <Card.Body>
                  <p>
                    <strong>Login.gov is a sign in service</strong> that offers secure and private access to <EVerify />
                    . You must have a Login.gov account and enable Multi-Factor Authentication (MFA) to continue your
                    access to <EVerify />.
                  </p>

                  {errorMessage && (
                    <Alert status="error" slim className="margin-top-4">
                      <Alert.Text>{errorMessage}</Alert.Text>
                    </Alert>
                  )}
                </Card.Body>

                <Card.Footer>
                  <ButtonGroup>
                    <a
                      className="usa-button usa-button--outline"
                      title="Click here to learn more about account migration to Login.gov in a new tab"
                      aria-label="Click here to learn more about account migration to Login.gov in a new tab"
                      target="_blank"
                      href={`${ExternalLinks.MFA_MIGRATION}`}
                      rel="noopener noreferrer"
                    >
                      Learn More
                    </a>
                    <Button
                      title="Click here to start the migration process on Login.gov in a new tab"
                      onClick={onMigrateClick}
                    >
                      Migrate Your Account
                    </Button>
                  </ButtonGroup>
                </Card.Footer>
              </Card>
            </CardGroup>
          </div>
        </main>

        <div className="width-full">
          <EveFooter />
        </div>
      </div>
    </>
  );
};

export const MfaMigrationWrapper: React.FC = () => {
  return (
    <UserProvider>
      <AccountProvider>
        <MfaMigration />
      </AccountProvider>
    </UserProvider>
  );
};

export default MfaMigrationWrapper;
